import { useState } from "react";
import { useT } from "../i18n";

/** What the server accepts, counted in characters (narration.MaxDirectionChars). */
export const MAX_DIRECTION_CHARS = 500;

/**
 * DirectionPicker is how a book says the way it should be read — "unhurried,
 * like an audiobook narrator" (ADR-0109 §4).
 *
 * A book setting, beside the voice and the part size, because the complaint it
 * answers was about a chapter's *delivery* and a book read two ways is not a
 * book. Changing it re-renders honestly and leaves existing audio alone
 * (ADR-0004), which is why the warning about re-recording lives on the Narrate
 * control next to the money, and not here.
 *
 * **Shown only where the model takes direction.** A model that is sent only the
 * text would speak a direction rather than follow it, so it gets no control at
 * all rather than one that quietly does nothing — the same rule the correction
 * sheet keeps for pronunciation hints (ADR-0064, ADR-0109). The server refuses
 * the field on such a model too, so this is showing the rule rather than
 * keeping it.
 *
 * Suggestions are chips, not a dropdown, and they fill the box rather than
 * replacing it: the point is to start from words somebody can then edit, on a
 * phone, without typing a sentence from nothing.
 */
export default function DirectionPicker({
  value,
  onChange,
  disabled = false,
}: {
  /** The book's direction, or "" for undirected. */
  value: string;
  onChange: (direction: string) => void;
  disabled?: boolean;
}) {
  const t = useT();
  // Shown as typed rather than as saved, and committed on blur. Bound straight
  // to the server it would save a character at a time; bound only to the
  // server's answer it would stay put through the round trip, which on a phone
  // reads as a control that did not take. The page keys this on the saved
  // value, so it starts again from whatever came back.
  const [draft, setDraft] = useState(value);
  const over = [...draft].length > MAX_DIRECTION_CHARS;

  const commit = (next: string) => {
    const trimmed = next.trim();
    if (trimmed !== value.trim() && [...trimmed].length <= MAX_DIRECTION_CHARS) {
      onChange(trimmed);
    }
  };

  return (
    <div className="mt-3" data-testid="direction-picker">
      <label className="block text-sm">
        <span className="mb-1 block text-xs text-muted">{t.voice.directionLabel}</span>
        {/* dir="auto" on the box holding authored text and nowhere inside it:
            a Persian direction must lay out right to left, and an element with
            dir="auto" skips descendants that carry their own direction when it
            looks for the first strong character. */}
        <textarea
          dir="auto"
          rows={2}
          data-testid="direction-input"
          value={draft}
          disabled={disabled}
          placeholder={t.voice.directionPlaceholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          className="w-full resize-y rounded-control border border-line bg-surface px-3 py-2 text-sm disabled:opacity-50"
        />
      </label>
      <p className="mt-1 text-xs text-muted" data-testid="direction-note">
        {t.voice.directionNote}
      </p>
      {over && (
        <p role="alert" className="mt-1 text-xs text-red-600" data-testid="direction-too-long">
          {t.voice.directionTooLong(MAX_DIRECTION_CHARS)}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {t.voice.directionSuggestions.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            data-testid="direction-suggestion"
            disabled={disabled}
            onClick={() => {
              setDraft(suggestion);
              commit(suggestion);
            }}
            className="rounded-full border border-line px-3 py-1 text-xs text-muted transition-colors hover:bg-sunk hover:text-ink disabled:opacity-50"
          >
            {suggestion}
          </button>
        ))}
        {draft.trim() !== "" && (
          <button
            type="button"
            data-testid="direction-clear"
            disabled={disabled}
            onClick={() => {
              setDraft("");
              commit("");
            }}
            className="rounded-full border border-line px-3 py-1 text-xs text-muted transition-colors hover:bg-sunk hover:text-ink disabled:opacity-50"
          >
            {t.voice.directionClear}
          </button>
        )}
      </div>
    </div>
  );
}
